import { Link } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import { useAppSelector } from '../../hooks/redux';

const MODULES = [
  { title: 'Users', desc: 'Every account with its companies. Disable a user or a single company in one click.', tag: 'People', to: '/users' },
  { title: 'Companies', desc: 'Invite companies, filter by country and stage, and control their access.', tag: 'People', to: '/companies' },
  { title: 'Partners', desc: 'The funding network that receives rated companies.', tag: 'Network', to: '/partners' },
  { title: 'Payments', desc: 'Every fee transaction, successful and pending, with invoice PDFs. Record bank transfers into the ledger.', tag: 'Revenue', to: '/payments' },
  { title: 'Fees', desc: 'Processing fee, success fee and tax per country — the web app pricing page reads these.', tag: 'Config', to: '/fees' },
  { title: 'Documents', desc: 'The grouped document checklist per country and the rating agent that validates each group.', tag: 'Config', to: '/documents' },
  { title: 'Ratings', desc: 'Per-country AI system prompt, guidelines and scoring settings, with live preview and sample tests.', tag: 'AI', to: '/ratings' },
  { title: 'AI Settings', desc: 'Model catalog, default model and connection tests for the AI platform.', tag: 'AI', to: '/ai-settings' },
];

type Summary = { role: string; isActive: boolean; count: number };

export default function AdminHome() {
  const user = useAppSelector((s) => s.auth.user);
  const users = useApi<{ summary: Summary[] }>('/admin/users', { limit: 1 });
  const docs = useApi<{ countries: { active: number; hasPrompt: boolean }[] }>('/admin/documents');
  const ai = useApi<{ providers: { label: string; isDefault: boolean }[] }>('/admin/ai/providers');

  const summary = users.data?.summary || [];
  const customers = summary.filter((s) => s.role === 'CUSTOMER').reduce((n, s) => n + s.count, 0);
  const disabled = summary.filter((s) => !s.isActive).reduce((n, s) => n + s.count, 0);
  const countries = docs.data?.countries || [];
  const stats = [
    { v: users.data ? customers : '…', l: 'Customers', hint: disabled ? `${disabled} disabled` : 'All active', to: '/users' },
    { v: docs.data ? countries.length : '…', l: 'Countries', hint: `${countries.reduce((n, c) => n + c.active, 0)} documents`, to: '/documents' },
    { v: docs.data ? countries.filter((c) => c.hasPrompt).length : '…', l: 'Rating prompts', hint: 'Configured countries', to: '/ratings' },
    { v: ai.data?.providers.find((p) => p.isDefault)?.label || '…', l: 'Default model', hint: 'AI platform catalog', to: '/ai-settings' },
  ];

  return (
    <div>
      <section
        className="mz-fade-up relative overflow-hidden rounded-2xl px-6 py-6 text-white sm:px-8"
        style={{ background: 'linear-gradient(135deg, #1a000f 0%, #7a0038 32%, #c4005e 62%, #e8007a 100%)', boxShadow: '0 16px 40px rgba(157,0,80,0.18)' }}
      >
        <div className="pointer-events-none absolute -right-8 -top-10 h-40 w-40 rounded-full" style={{ background: 'rgba(255,255,255,0.1)' }} />
        <div className="pointer-events-none absolute bottom-0 right-16 h-24 w-24 rounded-full" style={{ background: 'rgba(255,255,255,0.08)' }} />
        <div className="relative z-10 max-w-xl">
          <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-white/75">Control plane</p>
          <h1 className="mt-1.5 text-2xl font-black tracking-tight">Welcome{user?.name ? `, ${user.name.split(' ')[0]}` : ' to Micus Admin'}</h1>
          <p className="mt-2 text-sm leading-relaxed text-white/80">Manage users and companies, pricing, country document checklists and the AI rating platform from one console.</p>
        </div>
      </section>

      <div className="mz-fade-up mz-d1 mt-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
        {stats.map((s) => (
          <Link key={s.l} to={s.to} className="mz-card mz-card-link px-4 py-3.5">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-[#a77d92]">{s.l}</p>
            <p className="mt-1 truncate text-xl font-bold tracking-tight text-[#d6006e]">{s.v}</p>
            <p className="mt-0.5 text-xs text-[#a77d92]">{s.hint}</p>
          </Link>
        ))}
      </div>

      <div className="mz-fade-up mz-d2 mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {MODULES.map((mod) => (
          <Link key={mod.title} to={mod.to} className="mz-card mz-card-link group p-4">
            <div className="flex items-start justify-between gap-3">
              <h2 className="text-sm font-bold text-[#6b0030] transition-colors group-hover:text-[#d6006e]">{mod.title}</h2>
              <span className="rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider" style={{ background: '#fdf2f8', color: '#d6006e', border: '1px solid #fce7f3' }}>
                {mod.tag}
              </span>
            </div>
            <p className="mt-1.5 text-[13px] leading-relaxed text-[#a77d92]">{mod.desc}</p>
            <p className="mt-2 text-xs font-bold text-[#d6006e] opacity-0 transition-opacity group-hover:opacity-100">Open →</p>
          </Link>
        ))}
      </div>
    </div>
  );
}
